/**
 * `/$tenantSlug/approvals` — the Agent approval inbox (Surface 2 of
 * ui_tier1_surfaces). A first-class page where an owner approves or
 * rejects pending agent actions.
 *
 * Backend (ADR-0076): the L7 shell proxy of L5 —
 *   GET  /api/shell/approvals            → the pending list (oldest-first)
 *   GET  /api/shell/approvals/count      → {count} for the nav badge (poll 60s)
 *   POST /api/shell/approvals/{id}/decide  body {decision, note?}
 * The decide route reuses the ADR-051 step-up gate: a 401
 * {error_code:"step_up_required"} means re-enter the password, then retry.
 *
 * The list is read-only proxy data — no new tables. "Modify" is not
 * rendered (deferred per verb-gating §E); only Approve / Reject.
 *
 * A11y (design intent §G): the action heading is the stretched-link (G3);
 * status filter is aria-pressed toggle buttons + a live count, NOT a
 * tablist; decisions animate the card out (collapse under
 * prefers-reduced-motion) and announce "Approved — N remaining" in a live
 * region; loading skeleton is aria-hidden under an aria-busy container.
 */

import { RequirePermission, useCurrency } from "@ken/auth";
import { Button, Chip, EntityChip, entityRoute, Skeleton } from "@ken/ui";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";

import { HalfFinishedActions } from "../shell/HalfFinishedActions";
import { StepUpDialog } from "../shell/StepUpDialog";

export const Route = createFileRoute("/$tenantSlug/approvals")({
  component: ApprovalsRoute,
});

// ---------------------------------------------------------------------------
// Types — mirror the GET /api/shell/approvals item shape (ADR-0076).
// ---------------------------------------------------------------------------

interface EvidenceItem {
  entity_type: string;
  entity_id: string | null;
  label: string;
}

interface ApprovalItem {
  approval_id: string;
  action_intent_id: string;
  action_type: string;
  action_summary: string;
  requested_at: string;
  expires_at: string | null;
  urgency: string | null;
  payload_preview: { evidence?: EvidenceItem[] } & Record<string, unknown>;
  tier: number;
  dollar_exposure: number | null;
}

type FilterMode = "pending" | "decided" | "all";

// ---------------------------------------------------------------------------
// Page wrapper — permission-gated.
// ---------------------------------------------------------------------------

function ApprovalsRoute(): ReactElement {
  return (
    <RequirePermission slug="shell.approvals.view">
      <ApprovalsInbox />
    </RequirePermission>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function humanizeActionType(actionType: string): string {
  return actionType
    .split(/[._]/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function formatMoney(n: number, currency: string): string {
  return n.toLocaleString("en-US", { style: "currency", currency });
}

function expiresInLabel(iso: string | null): { label: string; datetime: string } | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return { label: "Expired", datetime: iso };
  const mins = Math.round(ms / 60000);
  if (mins < 60) return { label: `Expires in ${mins} min`, datetime: iso };
  const hours = Math.round(mins / 60);
  if (hours < 48) return { label: `Expires in ${hours} h`, datetime: iso };
  return { label: `Expires in ${Math.round(hours / 24)} d`, datetime: iso };
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

function ApprovalsInbox(): ReactElement {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };
  const slug = tenantSlug ?? "";

  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<FilterMode>("pending");
  const [announce, setAnnounce] = useState("");

  // Step-up: when a decide call 401s with step_up_required, hold the
  // pending decision until the password is re-entered, then retry it.
  const [stepUp, setStepUp] = useState<
    | {
        approvalId: string;
        decision: "approved" | "rejected";
        note?: string;
      }
    | null
  >(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetch("/api/shell/approvals", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`load failed ${res.status}`);
      const data = (await res.json()) as ApprovalItem[];
      // Oldest-first.
      data.sort(
        (a, b) =>
          new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime(),
      );
      setItems(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The decide call. Returns "step_up" when the gate fires so the caller
  // can open the password dialog and replay on success.
  const decide = useCallback(
    async (
      approvalId: string,
      decision: "approved" | "rejected",
      note?: string,
      password?: string,
    ): Promise<"ok" | "step_up" | "error"> => {
      try {
        const res = await fetch(`/api/shell/approvals/${approvalId}/decide`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            decision,
            ...(note ? { note } : {}),
            ...(password ? { password } : {}),
          }),
        });
        if (res.status === 401) {
          const body = (await res.json().catch(() => ({}))) as {
            error_code?: string;
          };
          if (body.error_code === "step_up_required") return "step_up";
          return "error";
        }
        if (!res.ok) return "error";
        return "ok";
      } catch {
        return "error";
      }
    },
    [],
  );

  const onDecided = useCallback(
    (approvalId: string, decision: "approved" | "rejected") => {
      setItems((prev) => {
        const remaining = prev.filter((i) => i.approval_id !== approvalId);
        setAnnounce(
          `${decision === "approved" ? "Approved" : "Rejected"} — ${remaining.length} remaining`,
        );
        return remaining;
      });
    },
    [],
  );

  const handleDecision = useCallback(
    async (
      approvalId: string,
      decision: "approved" | "rejected",
      note?: string,
    ): Promise<"ok" | "step_up" | "error"> => {
      const result = await decide(approvalId, decision, note);
      if (result === "ok") onDecided(approvalId, decision);
      if (result === "step_up") setStepUp({ approvalId, decision, ...(note ? { note } : {}) });
      return result;
    },
    [decide, onDecided],
  );

  const retryAfterStepUp = useCallback(
    async (password: string): Promise<"ok" | "error"> => {
      if (!stepUp) return "error";
      const result = await decide(
        stepUp.approvalId,
        stepUp.decision,
        stepUp.note,
        password,
      );
      if (result === "ok") {
        onDecided(stepUp.approvalId, stepUp.decision);
        setStepUp(null);
        return "ok";
      }
      return "error";
    },
    [stepUp, decide, onDecided],
  );

  return (
    <div data-testid="shell-approvals-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-[24px] font-medium tracking-tight text-[var(--color-text-strong)]">
          Approvals
        </h1>
        <p className="text-sm text-[var(--color-text-soft)]">
          Ken asks before it acts. Review what&apos;s waiting on you.
        </p>
      </header>

      {/* Actions that half-landed and could not be undone (PRD 01 D5). Renders
          nothing when there are none, so its presence always means something.
          It sits above the pending list because it is the more urgent thing:
          those are decisions still to make, these already happened. */}
      <HalfFinishedActions />

      {/* Status filter — aria-pressed toggles + live count, NOT a tablist. */}
      <div className="flex items-center gap-2" role="group" aria-label="Filter approvals">
        {(["pending", "decided", "all"] as FilterMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            aria-pressed={filter === mode}
            data-testid={`shell-approvals-filter-${mode}`}
            onClick={() => setFilter(mode)}
            className={
              "px-3 py-1.5 text-[13px] font-medium rounded-[var(--radius-full)] border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] " +
              (filter === mode
                ? "bg-[var(--color-brand-soft)] border-transparent text-[var(--color-brand-strong)]"
                : "bg-[var(--color-surface)] border-[var(--color-border-hairline)] text-[var(--color-text-soft)] hover:text-[var(--color-text-strong)]")
            }
          >
            {mode === "pending" ? "Pending" : mode === "decided" ? "Decided" : "All"}
          </button>
        ))}
        <span
          aria-live="polite"
          className="ml-1 text-[13px] text-[var(--color-text-faint)] tabular-nums"
        >
          {loading ? "" : `${items.length} pending`}
        </span>
      </div>

      {/* Live region for decision outcomes (focus moves separately). */}
      <div aria-live="polite" className="sr-only" data-testid="shell-approvals-live">
        {announce}
      </div>

      {loading ? (
        <div
          data-testid="shell-approvals-loading"
          aria-busy="true"
          className="flex flex-col gap-4"
        >
          {[0, 1].map((i) => (
            <div
              key={i}
              className="rounded-[var(--radius-lg)] border border-[var(--color-border-hairline)] p-5 flex flex-col gap-3"
            >
              <Skeleton className="h-5 w-2/5" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-9 w-40" />
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div
          data-testid="shell-approvals-error"
          role="alert"
          className="flex flex-col items-start gap-3 py-6"
        >
          <p className="text-sm text-[var(--color-status-overdue-text)]">
            Couldn&apos;t load approvals.
          </p>
          <Button
            variant="solid"
            size="sm"
            data-testid="shell-approvals-error-retry"
            onClick={() => void load()}
          >
            Retry
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="shell-approvals-empty"
          className="py-12 text-center text-sm text-[var(--color-text-soft)]"
        >
          Nothing waiting on you. Ken will ask before acting.
        </div>
      ) : (
        <ApprovalList
          items={items}
          tenantSlug={slug}
          onDecision={handleDecision}
        />
      )}

      {stepUp ? (
        <StepUpDialog
          onCancel={() => setStepUp(null)}
          onSubmit={retryAfterStepUp}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApprovalList
// ---------------------------------------------------------------------------

function ApprovalList({
  items,
  tenantSlug,
  onDecision,
}: {
  items: ApprovalItem[];
  tenantSlug: string;
  onDecision: (
    approvalId: string,
    decision: "approved" | "rejected",
    note?: string,
  ) => Promise<"ok" | "step_up" | "error">;
}): ReactElement {
  return (
    <ul className="flex flex-col gap-4 list-none p-0 m-0">
      {items.map((item) => (
        <li key={item.approval_id}>
          <ApprovalCard
            item={item}
            tenantSlug={tenantSlug}
            onDecision={onDecision}
          />
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// ApprovalCard
// ---------------------------------------------------------------------------

function ApprovalCard({
  item,
  tenantSlug,
  onDecision,
}: {
  item: ApprovalItem;
  tenantSlug: string;
  onDecision: (
    approvalId: string,
    decision: "approved" | "rejected",
    note?: string,
  ) => Promise<"ok" | "step_up" | "error">;
}): ReactElement {
  const currency = useCurrency();
  const [showReject, setShowReject] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const headingRef = useRef<HTMLAnchorElement>(null);

  const heading = humanizeActionType(item.action_type);
  const expires = expiresInLabel(item.expires_at);
  const evidence = item.payload_preview?.evidence ?? [];
  // The card's deep-link target: route to the first piece of evidence if it
  // is itself linkable; otherwise the heading is a non-link (still the H,
  // just not stretched). G3 prefers a real link in the heading.
  const primary = evidence.find(
    (e) => e.entity_id != null && entityRoute(e.entity_type, e.entity_id, tenantSlug),
  );
  const headingHref = primary
    ? entityRoute(primary.entity_type, primary.entity_id, tenantSlug)
    : null;

  async function approve(): Promise<void> {
    setDeciding(true);
    const result = await onDecision(item.approval_id, "approved");
    setDeciding(false);
    if (result === "ok") setLeaving(true);
  }

  async function confirmReject(note: string): Promise<void> {
    setDeciding(true);
    const result = await onDecision(item.approval_id, "rejected", note);
    setDeciding(false);
    if (result === "ok") {
      setShowReject(false);
      setLeaving(true);
    }
  }

  return (
    <div
      data-testid={`approval-card-${item.approval_id}`}
      className={
        "relative rounded-[var(--radius-lg)] border border-[var(--color-border-hairline)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-card)] flex flex-col gap-3 " +
        (leaving
          ? "motion-safe:transition-all motion-safe:duration-300 opacity-0 max-h-0 overflow-hidden p-0 border-0"
          : "")
      }
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[16px] font-medium text-[var(--color-text-strong)] min-w-0">
          {headingHref ? (
            <Link
              ref={headingRef}
              to={headingHref as never}
              className="after:absolute after:inset-0 hover:text-[var(--color-brand-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)] rounded-[var(--radius-sm)]"
            >
              {heading}
            </Link>
          ) : (
            <span>{heading}</span>
          )}
        </h2>
        <div className="flex items-center gap-1.5 shrink-0 relative z-10">
          <TierChip tier={item.tier} />
          {item.urgency ? <UrgencyChip urgency={item.urgency} /> : null}
        </div>
      </div>

      {expires ? (
        <div className="flex items-center gap-1.5 text-[12px] text-[var(--color-text-soft)]">
          <Clock size={12} aria-hidden="true" />
          <time dateTime={expires.datetime}>{expires.label}</time>
        </div>
      ) : null}

      {/* Reasoning — never one-line-truncated. */}
      <p className="text-sm text-[var(--color-text-soft)] whitespace-pre-line">
        {item.action_summary}
      </p>

      {evidence.length > 0 ? (
        <div
          data-testid={`approval-card-${item.approval_id}-evidence`}
          className="flex flex-wrap gap-2 relative z-10"
        >
          {evidence.map((e, i) => {
            const href = entityRoute(e.entity_type, e.entity_id, tenantSlug);
            return (
              <EntityChip
                key={`${e.entity_id ?? "x"}-${i}`}
                entityType={e.entity_type}
                label={e.label}
                href={href}
                LinkComponent={Link as never}
                data-testid={`approval-card-${item.approval_id}-evidence-${i}`}
              />
            );
          })}
        </div>
      ) : null}

      {item.dollar_exposure != null ? (
        <div
          data-testid={`approval-card-${item.approval_id}-exposure`}
          className="text-[13px] text-[var(--color-text-soft)]"
        >
          Exposure:{" "}
          <span className="tabular-nums font-medium text-[var(--color-text-strong)]">
            {formatMoney(item.dollar_exposure, currency)}
          </span>
        </div>
      ) : null}

      <div className="flex items-center gap-2 pt-1 relative z-10">
        <Button
          variant="primary"
          size="sm"
          data-testid={`approval-card-${item.approval_id}-approve`}
          disabled={deciding}
          onClick={() => void approve()}
        >
          Approve
        </Button>
        <Button
          variant="solid"
          size="sm"
          data-testid={`approval-card-${item.approval_id}-reject`}
          disabled={deciding}
          onClick={() => setShowReject(true)}
        >
          Reject
        </Button>
      </div>

      {showReject ? (
        <RejectDialog
          onCancel={() => setShowReject(false)}
          onConfirm={confirmReject}
          deciding={deciding}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier + Urgency chips
// ---------------------------------------------------------------------------

function TierChip({ tier }: { tier: number }): ReactElement {
  // tier2 = attention (amber), tier3 = negative (red).
  if (tier >= 3) return <Chip tone="overdue">Tier 3</Chip>;
  if (tier === 2) return <Chip tone="trial">Tier 2</Chip>;
  return <Chip tone="default">{`Tier ${tier}`}</Chip>;
}

function UrgencyChip({ urgency }: { urgency: string }): ReactElement {
  const u = urgency.toLowerCase();
  if (u === "high" || u === "urgent") return <Chip tone="overdue">{cap(u)}</Chip>;
  if (u === "medium") return <Chip tone="trial">{cap(u)}</Chip>;
  return <Chip tone="default">{cap(u)}</Chip>;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// RejectDialog — reason capture. Plain controlled dialog (the page owns the
// overlay); Esc cancels, focus moves to the textarea.
// ---------------------------------------------------------------------------

function RejectDialog({
  onCancel,
  onConfirm,
  deciding,
}: {
  onCancel: () => void;
  onConfirm: (note: string) => void;
  deciding: boolean;
}): ReactElement {
  const [note, setNote] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => textareaRef.current?.focus());
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    onConfirm(note.trim());
  }

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-[oklch(0%_0_0_/_0.32)] p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Reject action"
        data-testid="shell-approvals-reject-dialog"
        className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-surface)] border border-[var(--color-border-hairline)] shadow-[var(--shadow-glass-deep)] p-5"
      >
        <form onSubmit={submit}>
          <h2 className="text-[16px] font-medium text-[var(--color-text-strong)] mb-1">
            Reject this action?
          </h2>
          <p className="text-sm text-[var(--color-text-soft)] mb-3">
            Add an optional reason. Ken records it on the audit trail.
          </p>
          <label
            htmlFor="reject-reason"
            className="block text-[12px] text-[var(--color-text-soft)] mb-1"
          >
            Reason
          </label>
          <textarea
            ref={textareaRef}
            id="reject-reason"
            data-testid="shell-approvals-reject-reason"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-hairline)] p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]"
          />
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="ghost"
              size="md"
              data-testid="shell-approvals-reject-cancel"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              size="md"
              data-testid="shell-approvals-reject-confirm"
              disabled={deciding}
            >
              Reject
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

